import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { authService } from '../services/api.service';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    const savedEmail = location.state?.email || sessionStorage.getItem('resetEmail');
    const savedOtp = location.state?.otp || sessionStorage.getItem('verifiedOtp');

    if (!savedEmail || !savedOtp) {
      navigate('/admin/forgot-password');
      return;
    }
    setEmail(savedEmail);
    setOtp(savedOtp);
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters long.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await authService.resetPassword(email, otp, newPassword);
      setSuccessMsg(response.message || 'Password reset successful!');

      sessionStorage.removeItem('resetEmail');
      sessionStorage.removeItem('verifiedOtp');

      setTimeout(() => {
        navigate('/admin/login', { state: { message: 'Password updated successfully. Please log in with your new password.' } });
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="infra-login-wrapper">
      {/* Left Split Hero Branding */}
      <div className="hero-section">
        <div className="hero-glow-effect"></div>
        <div className="hero-wave-overlay"></div>

        <div className="hero-content-layer">
          <div className="hero-logo">
            <div className="dots-grid-logo">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="dot-red"></div>
              ))}
            </div>
            <div className="logo-text-container">
              <span className="logo-main-text">INFRAEDGE</span>
              <span className="logo-sub-text">RED LIGHT THERAPY</span>
            </div>
          </div>

          <div className="hero-main-body">
            <h1 className="hero-headline">
              Set New <span className="hero-headline-highlight">Password</span>
            </h1>
            <p className="hero-subheadline">
              Create a new secure password for your InfraEdge admin control panel account.
            </p>
          </div>

          <div className="hero-footer-text" style={{ fontSize: '12px', color: '#64748b' }}>
            © 2026 InfraEdge Red Light Therapy Inc. All rights reserved.
          </div>
        </div>

      </div>

      {/* Right Split Form */}
      <div className="form-section">
        <div className="login-form-card">
          <div className="form-header">
            <h2 className="form-title">Reset Password</h2>
            <p className="form-subtitle">Enter your new admin account password below.</p>
          </div>

          {error && (
            <div className="form-alert">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div className="form-alert" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' }}>
              <CheckCircle2 size={16} />
              <span>{successMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label">New Password</label>
              <div className="input-field-wrapper">
                <Lock className="field-icon" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="styled-input"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Confirm New Password</label>
              <div className="input-field-wrapper">
                <Lock className="field-icon" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="styled-input"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn-red-submit" disabled={loading}>
              {loading ? (
                <div className="spinner-white"></div>
              ) : (
                <span>Reset Password & Log In</span>
              )}
            </button>
          </form>

          <div style={{ marginTop: '24px', textAlignment: 'center' }}>
            <Link to="/admin/login" style={{ color: '#64748b', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
